// Centralized idle-session config + helpers, shared by the proactive
// SessionManager and the request layer (httpService).
//
// The session is kept alive — via silent id_token refresh — for as long as the
// user has interacted within IDLE_LIMIT_MS. A real user interaction updates the
// `lastActivity` timestamp; a token refresh deliberately does NOT, so an idle
// user is eventually logged out even though refreshes were succeeding while
// they were active.

/** Auto-logout once the user has been inactive for this long. */
export const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 minutes

/** Proactively refresh the id_token once it is within this window of expiry. */
export const REFRESH_BEFORE_MS = 2 * 60 * 1000; // 2 minutes

const LAST_ACTIVITY_KEY = "lastActivity";

/** Record that the user just interacted (resets the idle clock). */
export const markActivity = (): void => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  } catch {
    /* storage unavailable — ignore */
  }
};

export const getLastActivity = (): number => {
  const v = localStorage.getItem(LAST_ACTIVITY_KEY);
  const n = v ? Number(v) : 0;
  return Number.isFinite(n) ? n : 0;
};

/** True when the user has interacted within the idle window. */
export const isWithinIdleWindow = (): boolean => {
  const last = getLastActivity();
  if (!last) return false;
  return Date.now() - last < IDLE_LIMIT_MS;
};

/** Epoch-ms of a JWT's `exp` claim, or 0 if missing/unparseable. */
export const getTokenExpMs = (token: string): number => {
  try {
    const [, payloadBase64] = token.split(".");
    const payload = JSON.parse(atob(payloadBase64));
    return payload?.exp ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
};
